import { defineStore } from "pinia";
import userViewService from "../services/UserViewService.js";

/**
 * @typedef {Object} ViewCriterion
 * @property {number} id - Unique identifier for the criterion.
 * @property {Object} property - The property to search on.
 * @property {boolean} negation - Whether the criterion is negated.
 * @property {string} operator - The search operator (e.g., 'exact', 'contains', 'range').
 * @property {*} value - The search value.
 * @property {*|null} value_end - The end value for range searches.
 */

/**
 * @typedef {Object} ViewLocation
 * @property {number} id - Unique identifier for the location.
 * @property {'Site'|'Folder'|null} type - The type of location.
 * @property {string} value - The value for the location (e.g., site ID or nodeRef).
 * @property {boolean} negation - Whether the location is excluded (true) or included (false).
 * @property {string} label - Human-readable label for the location.
 */

/**
 * @typedef {Object} CriteriaGroup
 * @property {string} id - Unique identifier for the group.
 * @property {'AND'|'OR'} operator - Operator between criteria within the group.
 * @property {ViewCriterion[]} criteria - The list of search criteria in the group.
 */

/**
 * @typedef {Object} CriteriaGroups
 * @property {'AND'|'OR'} rootOperator - Operator between groups.
 * @property {CriteriaGroup[]} groups - The list of criteria groups.
 */

/**
 * @typedef {Object} SavedView
 * @property {string} id - Unique identifier for the saved view.
 * @property {string} name - The name of the saved view.
 * @property {string} [description] - An optional description for the view.
 * @property {'user'|'global'} scope - The scope of the view.
 * @property {boolean} [isFavorite] - Whether the view is marked as a favorite.
 * @property {Object} config - The view configuration.
 * @property {ViewLocation[]} config.locations - The location criteria (array).
 * @property {CriteriaGroups} [config.criteriaGroups] - The grouped criteria (new format).
 * @property {ViewCriterion[]} [config.criteria] - The list of search criteria (legacy flat format).
 */

/**
 * Pinia store for managing the state of the user view feature.
 */
export const useUserViewStore = defineStore("userView", {
  /**
   * Defines the state for the user view store.
   * @returns {{
   *   locations: ViewLocation[],
   *   criteriaGroups: CriteriaGroups,
   *   loadedView: SavedView|null,
   *   favoritesNeedReload: boolean
   * }}
   */
  state: () => ({
    /** @type {ViewLocation[]} */
    locations: [],
    /** @type {CriteriaGroups} */
    criteriaGroups: {
      rootOperator: "OR",
      groups: [],
    },
    /** @type {SavedView|null} */
    loadedView: null,
    /** @type {Array} - Holds the model for the favorite views menu. */
    favoriteViews: [],
    /** @type {Array} - Holds the list of personal saved views. */
    personalViews: [],
    /** @type {Array} - Holds the list of global saved views. */
    globalViews: [],
    /** @type {boolean} - Loading state for the saved views lists. */
    viewsLoading: true,
  }),
  /**
   * Getters for the user view store.
   */
  getters: {
    /**
     * Checks if a saved view is currently loaded.
     * @param {Object} state - The store's state.
     * @returns {boolean} - True if a view is loaded, false otherwise.
     */
    isSavedViewLoaded(state) {
      return state.loadedView !== null;
    },
    /**
     * Backward-compatible getter: returns the first location or a default empty object.
     * @param {Object} state - The store's state.
     * @returns {ViewLocation|{type: null, value: string}}
     */
    location(state) {
      if (state.locations.length > 0) {
        return state.locations[0];
      }
      return { type: null, value: "" };
    },
    /**
     * Backward-compatible getter: flattens all criteria from all groups.
     * @param {Object} state - The store's state.
     * @returns {ViewCriterion[]}
     */
    allCriteria(state) {
      return state.criteriaGroups.groups.flatMap((g) => g.criteria);
    },
    /**
     * Backward-compatible alias for allCriteria (used by AdvancedSearchPanel).
     * @param {Object} state - The store's state.
     * @returns {ViewCriterion[]}
     */
    criteria(state) {
      return state.criteriaGroups.groups.flatMap((g) => g.criteria);
    },
  },
  /**
   * Actions for the user view store.
   */
  actions: {
    /**
     * Loads a saved view into the store, updating locations, criteria, and the loaded view reference.
     * Supports both old single-location format (config.location) and new multi-location format (config.locations).
     * @param {SavedView} savedView - The saved view object to load.
     */
    loadView(savedView) {
      if (!savedView || !savedView.config) {
        console.error(
          "Invalid saved view object provided to loadView",
          savedView,
        );
        return;
      }
      // Support both formats: locations[] (new) and location (legacy)
      if (
        savedView.config.locations &&
        Array.isArray(savedView.config.locations)
      ) {
        this.locations = savedView.config.locations;
      } else if (savedView.config.location) {
        // Legacy single-location format: convert to array
        const loc = savedView.config.location;
        if (loc.type && loc.value) {
          this.locations = [
            {
              id: Date.now(),
              type: loc.type,
              value: loc.value,
              negation: loc.negation || false,
              label: loc.label || loc.value,
            },
          ];
        } else {
          this.locations = [];
        }
      } else {
        this.locations = [];
      }
      // Support criteriaGroups (new) and criteria (legacy flat format)
      // Always force rootOperator=OR (between groups) and operator=AND (within groups)
      if (savedView.config.criteriaGroups) {
        const loaded = savedView.config.criteriaGroups;
        this.criteriaGroups = {
          rootOperator: "OR",
          groups: (loaded.groups || []).map((g) => ({
            ...g,
            operator: "AND",
          })),
        };
      } else if (
        savedView.config.criteria &&
        savedView.config.criteria.length > 0
      ) {
        // Migrate flat criteria to a single AND group
        this.criteriaGroups = {
          rootOperator: "OR",
          groups: [
            {
              id: "migrated",
              operator: "AND",
              criteria: savedView.config.criteria,
            },
          ],
        };
      } else {
        this.criteriaGroups = { rootOperator: "OR", groups: [] };
      }
      this.loadedView = savedView;
    },
    /**
     * Adds a new location to the locations list.
     * @param {ViewLocation} loc - The location to add.
     */
    addLocation(loc) {
      this.locations.push({
        ...loc,
        id: loc.id || Date.now(),
      });
    },
    /**
     * Removes a location from the locations list by its index.
     * @param {number} index - The index of the location to remove.
     */
    removeLocation(index) {
      this.locations.splice(index, 1);
    },
    /**
     * Sets the location for the user view (backward-compatible).
     * Replaces all locations with a single one.
     * @param {ViewLocation} newLocation - The new location object.
     */
    setLocation(newLocation) {
      if (newLocation && newLocation.type && newLocation.value) {
        this.locations = [
          {
            id: Date.now(),
            type: newLocation.type,
            value: newLocation.value,
            negation: newLocation.negation || false,
            label: newLocation.label || newLocation.value,
          },
        ];
      } else {
        this.locations = [];
      }
    },
    /**
     * Sets the criteria for the user view (legacy, wraps into single group).
     * This action also resets the currently loaded view.
     * @param {ViewCriterion[]} newCriteria - The new array of criteria.
     */
    setCriteria(newCriteria) {
      if (newCriteria && newCriteria.length > 0) {
        this.criteriaGroups = {
          rootOperator: "OR",
          groups: [
            { id: String(Date.now()), operator: "AND", criteria: newCriteria },
          ],
        };
      } else {
        this.criteriaGroups = { rootOperator: "OR", groups: [] };
      }
      this.loadedView = null;
    },
    /**
     * Adds a new empty criteria group.
     * @param {'AND'|'OR'} [operator='AND'] - The operator for criteria within the group.
     */
    addGroup(operator = "AND") {
      this.criteriaGroups.groups.push({
        id: String(Date.now()),
        operator,
        criteria: [],
      });
    },
    /**
     * Removes a criteria group by index.
     * @param {number} groupIndex - The index of the group to remove.
     */
    removeGroup(groupIndex) {
      this.criteriaGroups.groups.splice(groupIndex, 1);
    },
    /**
     * Adds a criterion to a specific group.
     * @param {number} groupIndex - The index of the group.
     * @param {ViewCriterion} criterion - The criterion to add.
     */
    addCriterionToGroup(groupIndex, criterion) {
      if (this.criteriaGroups.groups[groupIndex]) {
        this.criteriaGroups.groups[groupIndex].criteria.push(criterion);
      }
    },
    /**
     * Removes a criterion from a specific group.
     * @param {number} groupIndex - The index of the group.
     * @param {number} criterionIndex - The index of the criterion within the group.
     */
    removeCriterionFromGroup(groupIndex, criterionIndex) {
      if (this.criteriaGroups.groups[groupIndex]) {
        this.criteriaGroups.groups[groupIndex].criteria.splice(
          criterionIndex,
          1,
        );
      }
    },
    /**
     * Updates a criterion in a specific group.
     * @param {number} groupIndex - The index of the group.
     * @param {number} criterionIndex - The index of the criterion within the group.
     * @param {ViewCriterion} updated - The updated criterion.
     */
    updateCriterionInGroup(groupIndex, criterionIndex, updated) {
      if (this.criteriaGroups.groups[groupIndex]) {
        this.criteriaGroups.groups[groupIndex].criteria.splice(
          criterionIndex,
          1,
          updated,
        );
      }
    },
    /**
     * Backward-compatible: adds a criterion to the first group (creates one if needed).
     * Used by AdvancedSearchPanel (legacy).
     * @param {ViewCriterion} criterion - The criterion to add.
     */
    addCriterion(criterion) {
      if (this.criteriaGroups.groups.length === 0) {
        this.addGroup("AND");
      }
      this.criteriaGroups.groups[0].criteria.push(criterion);
    },
    /**
     * Backward-compatible: removes a criterion by flat index across all groups.
     * Used by AdvancedSearchPanel (legacy).
     * @param {number} index - The flat index of the criterion to remove.
     */
    removeCriterion(index) {
      let remaining = index;
      for (const group of this.criteriaGroups.groups) {
        if (remaining < group.criteria.length) {
          group.criteria.splice(remaining, 1);
          return;
        }
        remaining -= group.criteria.length;
      }
    },
    /**
     * Resets the entire user view state to its default values.
     */
    resetUserView() {
      this.locations = [];
      this.criteriaGroups = { rootOperator: "OR", groups: [] };
      this.loadedView = null;
    },
    /**
     * Saves the current view configuration as a new personal or global view.
     * @param {SavedView} viewToSave - The view object to save.
     */
    async saveCurrentView(viewToSave) {
      try {
        await userViewService.saveView(viewToSave);
        this.loadView(viewToSave); // Make the saved view the current one
      } catch (error) {
        console.error("Error saving the current view:", error);
        throw error;
      }
    },

    /**
     * Updates an existing saved view with the current configuration.
     * @param {SavedView} viewToUpdate - The view object with updated information.
     */
    async updateCurrentView(viewToUpdate) {
      try {
        const updatedView = await userViewService.updateView(viewToUpdate);
        this.loadView(updatedView.entry || updatedView);
      } catch (error) {
        console.error("Error updating the current view:", error);
        throw error;
      }
    },
    /**
     * Deletes the currently loaded saved view.
     * @returns {Promise<void>}
     */
    async deleteCurrentView() {
      if (!this.loadedView) {
        throw new Error("No view loaded to delete.");
      }
      try {
        await userViewService.deleteView(this.loadedView);
        this.resetUserView();
      } catch (error) {
        console.error("Error deleting the current view:", error);
        throw error;
      }
    },

    /**
     * Toggles the favorite status of a given view.
     * @param {SavedView} view - The view to toggle.
     * @returns {Promise<void>}
     */
    async toggleViewFavorite(view) {
      if (!view || !view.id) {
        throw new Error("Invalid view provided to toggleFavorite action.");
      }
      try {
        await userViewService.toggleFavoriteStatus(view);
        await this.fetchFavoriteViews();
      } catch (error) {
        console.error("Error toggling favorite status in store:", error);
        throw error;
      }
    },

    /**
     * Fetches all personal and global favorite views and formats them for menu display.
     */
    async fetchFavoriteViews() {
      this.viewsLoading = true;
      try {
        const [personalViews, globalViews, userConfig] = await Promise.all([
          userViewService.getPersonalViews(),
          userViewService.getGlobalViews(),
          userViewService.getPersonalViewsAndGlobalFavorites(),
        ]);

        const globalFavorites = userConfig?.globalFavoriteView || [];

        const favoritePersonal = (personalViews || []).filter(
          (s) => s.isFavorite,
        );

        const favoriteGlobal = (globalViews || []).filter((s) =>
          globalFavorites.some((fav) => fav.id === s.id),
        );

        this.personalViews = personalViews;
        this.globalViews = globalViews;

        const formatForMenu = (view) => ({
          label: view.name,
          to: `/vues/${view.scope}/${view.id}`,
        });

        this.favoriteViews = [
          ...favoritePersonal.map(formatForMenu),
          ...favoriteGlobal.map(formatForMenu),
        ];
      } catch (error) {
        console.error("Error fetching favorite views:", error);
        this.favoriteViews = [];
      } finally {
        this.viewsLoading = false;
      }
    },

    async fetchSavedViews() {
      this.viewsLoading = true;
      try {
        const results = await Promise.allSettled([
          userViewService.getPersonalViews(),
          userViewService.getGlobalViews(),
        ]);

        if (results[0].status === "fulfilled") {
          this.personalViews = results[0].value;
        }

        if (results[1].status === "fulfilled") {
          this.globalViews = results[1].value;
        }
      } catch (error) {
        console.error("Error fetching views:", error);
      } finally {
        this.viewsLoading = false;
      }
    },
  },
});
